import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { auth } from "../FirebaseConfig"; 
import { motion, AnimatePresence } from 'framer-motion'; 
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../FirebaseConfig";
import { doc, getDoc,  deleteDoc, Timestamp } from "firebase/firestore";

export default function ListingDetail() {
  const currentUser = auth.currentUser
  const canvasRef = useRef(null);
  const { listingId } = useParams();
  const [listing, setListing] = useState({ features: [] }); // Or similar structure based on your data
  const [agent, setAgent] = useState(null);
  const [thumbnails, setThumbnails] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

    // 3D Animation Setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true,
      antialias: true,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);

    const geometry = new THREE.TorusKnotGeometry(1, 0.4, 100, 16);
    const material = new THREE.MeshPhysicalMaterial({
      color: 0x60a5fa,
      clearcoat: 1.0,
      clearcoatRoughness: 0.2,
      roughness: 0.5,
      metalness: 0.8,
      transparent: true,
      opacity: 0.7,
    });
    const torusKnot = new THREE.Mesh(geometry, material);
    scene.add(torusKnot);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    const pointLight = new THREE.PointLight(0x3b82f6, 1, 100);
    pointLight.position.set(5, 5, 5);
    scene.add(pointLight);

    camera.position.z = 5;

    const animate = () => {
      requestAnimationFrame(animate);
      torusKnot.rotation.x += 0.005;
      torusKnot.rotation.y += 0.005;
      renderer.render(scene, camera);
    };

    animate();

    const onWindowResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
      renderer.dispose();
    };
  }, []);

      const formatPrice = (price) => {
    if (!price) return 'Price not specified';
    return `₦${Number(price).toLocaleString()}`;
  };
  useEffect(() => {
    const fetchListing = async () => {
      const listingRef = doc(db, "listings", listingId);
      const listingSnap = await getDoc(listingRef);
      if (listingSnap.exists()) {
        const data = listingSnap.data();
        setListing(data);
        setMainImage(data.images?.[0] || "");
        setThumbnails(data.images?.slice(1, 5) || []);

        const agentRef = doc(db, "users", data.postedBy);
        const agentSnap = await getDoc(agentRef);
        if (agentSnap.exists()) {
          setAgent(agentSnap.data());
        }
      }
    };
    fetchListing();
  }, [listingId]);

  const deleteListing = async () => {
    try {
      if (listing.status === 'taken') {
        throw new Error('This listing is already marked as taken.');
      }
      await deleteDoc(doc(db, 'listings', listingId));
      setShowDeleteModal(false);
      navigate('/');
    } catch (error) {
      console.error('Error deleting listing:', error);
      setError(`Failed to update listing: ${error.message}`);
    }
  };

  const handleImageClick = (clickedImg, index) => {
    const updatedThumbs = [...thumbnails];
    const currentMain = mainImage;
    updatedThumbs[index] = currentMain;
    setMainImage(clickedImg);
    setThumbnails(updatedThumbs);
  };

  if (!listing)
    return (
      <div className="text-center text-yellow-400 mt-10 text-lg animate-pulse">
        Loading listing...
      </div>
    );

  return (
    <div className="relative min-h-screen font-sans text-white bg-black/50">
      {/* 3D Animation Canvas */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 z-[-1] opacity-40 pointer-events-none"
      />

      {/* Main content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-10">
        {/* Main Image */}
        <motion.div
          className="rounded-3xl overflow-hidden shadow-2xl border border-white/10"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <img
            src={mainImage}
            alt="Main property"
            className="object-cover w-full h-80 sm:h-[30rem] transition-transform duration-500 hover:scale-[1.01]"
          />
        </motion.div>

        {/* Thumbnails */}
        {thumbnails.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            {thumbnails.map((img, i) => (
              <motion.img
                key={i}
                src={img}
                onClick={() => handleImageClick(img, i)}
                className="h-24 w-full rounded-xl object-cover border border-white/20 hover:scale-105 transition-transform cursor-pointer"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
              />
            ))}
          </div>
        )}

        {/* Title, Location, Price */}
        <div className="mt-8 border-b border-white/10 pb-4 space-y-2">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gold">
            {listing.title}
          </h1>
          <p className="text-gray-300 text-lg">{listing.location}</p>
          <p className="text-3xl text-green-400 font-bold mt-2">
            {formatPrice(listing.price)}
          </p>
        </div>

        {/* Main Info + Agent */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Left Section: Description + Amenities */}
          <div className="md:col-span-3 space-y-8">
            {/* Features */}
            <div className="flex gap-4 text-sm font-medium text-gray-200 flex-wrap">
              {listing?.features?.map((feature, i) => ( 
                <motion.div
                  key={i}
                  className="bg-white/10 px-5 py-2 rounded-xl border border-white/10 flex items-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  {feature.icon}
                  <span>
                    {feature.value} {feature.label}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gold mb-2">
                Property Description
              </h2>
              <p className="text-gray-300 leading-relaxed text-base">
                {listing.description}
              </p>
            </div>

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gold mb-2">
                  Key Amenities
                </h2>
                <ul className="list-disc ml-6 space-y-1 text-gray-300 text-sm sm:text-base">
                  {listing.amenities.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Agent Sidebar */}
          {agent && (
            <div className="bg-white/5 rounded-2xl p-6 backdrop-blur-xl border border-white/10 shadow-xl text-white space-y-6">
              <div className="flex items-center gap-4">
                <img
                  src={
                    agent.profilePic ||
                    `https://ui-avatars.com/api/?name=${agent.name}&background=0D8ABC&color=fff`
                  }
                  alt={agent.name}
                  className="w-16 h-16 rounded-full object-cover ring-2 ring-gold"
                />
                <div>
                  <h3 className="text-lg font-bold">Listing Agent</h3>
                  <p className="text-gray-300">{agent.name}</p>
                  <p className="text-sm text-gray-400">{agent.phoneNumber}</p>
                </div>
              </div>

              <motion.button
                onClick={() => navigate(`/chat/${agent.userId}`)}
                className="w-full bg-transparent hover:bg-white/10 border-2 border-gold text-white font-semibold py-3 rounded-xl transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Connect with Agent
              </motion.button>
              {currentUser?.uid === listing.postedBy &&
                listing.status !== 'taken' && (
                  <motion.button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Mark as Taken
                  </motion.button>
                )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white/10 backdrop-blur-xl p-8 rounded-2xl text-center max-w-sm w-full space-y-6 shadow-2xl border border-white/20"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-bold text-white">Confirm Action</h3>
              <p className="text-gray-300">
                Are you sure you want to mark this property as taken? This
                action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={deleteListing}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-xl font-semibold transition-colors"
                >
                  Confirm Taken
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-2 rounded-xl font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
